/**
 * @fileoverview `resolveBrandTagline` — per-locale brand tagline resolution (ESM).
 *
 * Mirrors `../resolveTagline.js` (CJS), which carries the canonical prose:
 * why the registry holds a per-language MAP rather than a resolved string
 * (miss-class 1 freezes copy in the boot locale), why it is a registry map
 * rather than a `t()` key (three catalogs, one of them absent entirely), and
 * why a locale-free caller passes `DEFAULT_TAGLINE_LANGUAGE` out loud.
 *
 * This is the copy every WEB app actually executes — Vite resolves the
 * package through the `import` / `browser` conditions — so a fix applied to
 * the CJS twin alone would never reach a rendered page.
 *
 * @module @puffin-core/brand-assets/resolveTagline
 */

/**
 * The language every brand is required to carry, and the fallback for any
 * language a brand has no copy for.
 *
 * @type {string}
 */
export const DEFAULT_TAGLINE_LANGUAGE = 'en';

/**
 * The visible prefix a PSEUDO-LOCALE stamps on every catalog-backed string.
 *
 * 🔴 A deliberate second copy of `PSEUDO_LOCALE_MARKER` from `@puffin-core/i18n-bootstrap` — this
 * package has ZERO dependencies by design (its CJS twin serves the API email renderers, which run in
 * plain CommonJS with no i18n stack). Guarded against drift by
 * `tests/cross-app/contracts/pseudoLocaleMarkerParity.contract.test.js` rather than tolerated.
 * See the CJS twin for the full rationale.
 *
 * @type {string}
 */
export const PSEUDO_LOCALE_TAGLINE_MARKER = '[RTL] ';

/**
 * Is this BCP-47 tag a PSEUDO-LOCALE?
 *
 * Recognised by SHAPE — a user-assigned region subtag (`XA`–`XZ`, the range ISO 3166-1 reserves for
 * private use, which is why CLDR/Chrome build `en-XA` and `ar-XB` on it). Keeps this package
 * dependency-free, and a future pseudo-locale needs no edit here.
 *
 * AAS-I18N-002 Q-W9-3 / ruling 37: because only the PRIMARY SUBTAG selects copy, `en-XA` resolved to
 * the `en` tagline and rendered UNMARKED — so twelve catalog-backed strings looked exactly like
 * hardcoded English and falsified the pseudo-locale's core promise.
 *
 * @param {string} language - A BCP-47 tag.
 * @returns {boolean} True when `language` carries a user-assigned region subtag.
 */
export function isPseudoLocaleTag(language) {
  if (typeof language !== 'string') {
    return false;
  }
  const [, region] = language.split('-');
  return typeof region === 'string' && /^X[A-Z]$/.test(region);
}

/**
 * Resolve a brand's tagline for a UI language.
 *
 * @param {{taglines: Record<string, string>}} brand - A BRAND_REGISTRY entry.
 * @param {string} [language] - Active UI language ('en', 'es', 'es-US', …).
 * @returns {string} The tagline in `language`, or the `en` tagline.
 * @throws {Error} If `brand` is not an object carrying a `taglines` object,
 *   or if that map has no `en` string.
 */
export function resolveBrandTagline(brand, language) {
  if (brand === null || typeof brand !== 'object') {
    throw new Error(
      `resolveBrandTagline: brand must be an object. Received: ${typeof brand} (${String(brand)}).`,
    );
  }
  const { taglines } = brand;
  if (taglines === null || typeof taglines !== 'object') {
    throw new Error(
      `resolveBrandTagline: brand "${String(brand.appKey)}" has no taglines map. ` +
        `Received: ${typeof taglines}.`,
    );
  }

  const primary =
    typeof language === 'string' && language.length > 0
      ? language.split('-')[0].toLowerCase()
      : DEFAULT_TAGLINE_LANGUAGE;

  // A pseudo-locale takes the DEFAULT-language copy and stamps it, so the string is visibly
  // catalog-backed. Applied once, at the end, so every return path is covered — the marker is a
  // property of the LOCALE, not of which fallback arm happened to win.
  const mark = (value) =>
    isPseudoLocaleTag(language) ? `${PSEUDO_LOCALE_TAGLINE_MARKER}${value}` : value;

  if (typeof taglines[primary] === 'string') {
    return mark(taglines[primary]);
  }
  if (typeof taglines[DEFAULT_TAGLINE_LANGUAGE] === 'string') {
    return mark(taglines[DEFAULT_TAGLINE_LANGUAGE]);
  }
  throw new Error(
    `resolveBrandTagline: brand "${String(brand.appKey)}" has no "${DEFAULT_TAGLINE_LANGUAGE}" tagline.`,
  );
}
